var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormControlLabel, Radio, Tooltip, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PlanType } from '../../../enum';
import { useTerminologyTranslation } from '../../app/terminology';
import { ConfirmDialog, RadioGroup, TableBody, TableCell, TableHead, TableRow } from '../../common';
import { formatTableHeaderReport } from '../../report/util';
import { LocationTableEntryMode } from '../constants';
import { useDetailTimeline, usePeriodLabelTimeline, usePlanType } from '../selectors';
import { ExtraColumnEmptyCells, ExtraColumnHeaderCells } from './extraColumns';
import { useEntryModeConversion } from './selectors';
var StyledRadioGroup = styled(RadioGroup)(function (_a) {
  var theme = _a.theme;
  return {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(3),
    flexWrap: 'nowrap',
    '& .MuiFormControlLabel-root': {
      margin: 0
    },
    '& .MuiFormControlLabel-label': {
      fontSize: theme.typography.pxToRem(14)
    },
    '& .MuiRadio-root': {
      padding: theme.spacing(0.5)
    }
  };
});
export var QuickInputTrigger = function (_a) {
  var disabled = _a.disabled,
    onClick = _a.onClick;
  var t = useTranslation('translation').t;
  return _jsxs(Typography, __assign({
    variant: "subtitle2",
    component: "span",
    sx: {
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      opacity: disabled ? 0.75 : 1
    },
    onClick: disabled ? undefined : onClick,
    "aria-disabled": disabled
  }, {
    children: [t('quickInput.title'), " ", _jsx(FontAwesomeIcon, {
      icon: faBolt
    })]
  }));
};
var EntryModeToggle = function (_a) {
  var entryMode = _a.entryMode,
    onChange = _a.onChange,
    disabled = _a.disabled,
    _b = _a.isAbsoluteDisabled,
    isAbsoluteDisabled = _b === void 0 ? false : _b;
  var t = useTranslation(['translation', 'wizard']).t;
  return _jsx(StyledRadioGroup, __assign({
    value: entryMode !== null && entryMode !== void 0 ? entryMode : LocationTableEntryMode.Percentage,
    onChange: onChange
  }, {
    children: [LocationTableEntryMode.Percentage, LocationTableEntryMode.Absolute].map(function (mode) {
      var isThisModeDisabled = mode === LocationTableEntryMode.Absolute && isAbsoluteDisabled;
      return _jsx(Tooltip, __assign({
        title: isThisModeDisabled ? t('wizard:attrition.absoluteDisabledTooltip') : '',
        placement: "top"
      }, {
        children: _jsx("span", {
          children: _jsx(FormControlLabel, {
            sx: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            },
            value: mode,
            control: _jsx(Radio, {}),
            label: t("wizard:bulkChange.".concat(mode)),
            disabled: disabled || isThisModeDisabled
          })
        })
      }), mode);
    })
  }));
};
export var LocationTableHeader = function (_a) {
  var _b, _c;
  var _d = _a.addColumns,
    addColumns = _d === void 0 ? [] : _d,
    stickySizes = _a.stickySizes,
    onQuickInputClick = _a.onQuickInputClick,
    disabled = _a.disabled,
    entryModeField = _a.entryModeField,
    name = _a.name,
    _e = _a.decimal,
    decimal = _e === void 0 ? 0 : _e,
    _f = _a.isAbsoluteDisabled,
    isAbsoluteDisabled = _f === void 0 ? false : _f,
    _g = _a.extraColumns,
    extraColumns = _g === void 0 ? [] : _g,
    _h = _a.usePeriodLabels,
    usePeriodLabels = _h === void 0 ? false : _h;
  var t = useTerminologyTranslation(['translation', 'wizard']).t;
  var planType = usePlanType();
  var detailTimelines = useDetailTimeline.apply(void 0, addColumns);
  var periodLabelTimelines = usePeriodLabelTimeline.apply(void 0, addColumns);
  var timelines = usePeriodLabels ? periodLabelTimelines : detailTimelines;
  var showToggle = !!entryModeField;
  // Conversion logic lives in the hook; the component only binds the handler
  var _j = useEntryModeConversion({
      name: name,
      entryModeField: entryModeField,
      decimal: decimal
    }),
    handleEntryModeChange = _j.handleEntryModeChange,
    entryMode = _j.entryMode,
    pendingEntryMode = _j.pendingEntryMode,
    handleConfirmEntryModeChange = _j.handleConfirmEntryModeChange,
    handleCancelEntryModeChange = _j.handleCancelEntryModeChange;
  var entryModeToggle = showToggle ? _jsx(EntryModeToggle, {
    entryMode: entryMode,
    onChange: handleEntryModeChange,
    disabled: disabled,
    isAbsoluteDisabled: isAbsoluteDisabled
  }) : null;
  return _jsxs(_Fragment, {
    children: [timelines.map(function (_a, rowIdx) {
      var titles = _a[0],
        mapType = _a[1];
      var Component = rowIdx ? TableBody : TableHead;
      return _jsx(Component, {
        children: _jsxs(TableRow, {
          children: [stickySizes && _jsxs(TableCell, __assign({
            greyColor: !!rowIdx,
            stickySizes: stickySizes,
            align: "center"
          }, {
            children: [rowIdx === 0 && onQuickInputClick && _jsx(QuickInputTrigger, {
              disabled: disabled,
              onClick: onQuickInputClick
            }), rowIdx === 1 && entryModeToggle]
          })), !!extraColumns.length && _jsx(ExtraColumnHeaderCells, {
            extraColumns: extraColumns,
            isSubRow: !!rowIdx
          }), titles.map(function (title, cellIdx, arr) {
            return _jsx(TableCell, __assign({
              greyColor: !!rowIdx,
              sx: {
                whiteSpace: 'nowrap',
                minWidth: '5rem'
              },
              highlightBorder: planType === PlanType.Monthly && !!timelines.length && !((cellIdx + 1) % (12 + addColumns.length)) && cellIdx !== arr.length - 1
            }, {
              children: formatTableHeaderReport(title)
            }), "".concat(title, "-").concat(cellIdx));
          })]
        })
      }, mapType);
    }), entryModeToggle && timelines.length < 2 && _jsx(TableBody, {
      children: _jsxs(TableRow, {
        children: [stickySizes && _jsx(TableCell, __assign({
          greyColor: true,
          stickySizes: stickySizes,
          align: "center"
        }, {
          children: entryModeToggle
        })), extraColumns.length > 0 && _jsx(ExtraColumnEmptyCells, {
          count: extraColumns.length
        }), ((_c = (_b = timelines[0]) === null || _b === void 0 ? void 0 : _b[0]) !== null && _c !== void 0 ? _c : []).map(function (title, cellIdx, titlesArr) {
          return _jsx(TableCell, {
            greyColor: true,
            highlightBorder: planType === PlanType.Monthly && !((cellIdx + 1) % (12 + addColumns.length)) && cellIdx !== titlesArr.length - 1
          }, "".concat(title, "-").concat(cellIdx));
        })]
      })
    }), _jsx(ConfirmDialog, {
      open: !!pendingEntryMode,
      title: "Warning",
      message: t('wizard:attrition.modeChangeWarning'),
      confirmButtonText: "continue",
      onConfirm: handleConfirmEntryModeChange,
      onClose: handleCancelEntryModeChange
    })]
  });
};